import { Injectable } from '@angular/core';

/**
 * EU data-residency ingestion host. Our Mixpanel project is EU-resident; events
 * sent to the default (US) host silently never arrive.
 */
const MIXPANEL_EU_API_HOST = 'https://api-eu.mixpanel.com';

/** Cap on buffered pre-init actions, so the buffer can't grow unbounded when load() never runs. */
const MAX_PENDING_ACTIONS = 50;

/** Super-properties attached to every event (AC: who + which entity). */
export interface MixpanelIdentityProperties {
  legalEntityId: string;
  legalEntityName: string;
  userRole: string;
  appVersion: string;
}

/** Init options we pass to Mixpanel, including Session Replay masking. */
interface MixpanelConfig {
  api_host: string;
  // String modes make the SDK watch SPA URL changes; `true` records only the first load.
  track_pageview: 'url-with-path' | 'full-url' | 'url-with-path-and-query-string' | boolean;
  persistence: 'localStorage';
  record_sessions_percent: number;
  record_mask_all_text: boolean;
  record_mask_all_inputs: boolean;
  record_console: boolean;
  record_network: boolean;
  record_block_selector: string;
}

/** The subset of the Mixpanel global API this service uses. */
interface MixpanelLib {
  init(token: string, config: MixpanelConfig): void;
  identify(distinctId: string): void;
  register(properties: Record<string, unknown>): void;
  track(eventName: string, properties?: Record<string, unknown>): void;
  reset(): void;
  __SV?: number;
}

declare global {
  interface Window {
    mixpanel?: MixpanelLib;
  }
}

/**
 * Loads the Mixpanel browser SDK (lender app only, gated on consent by the caller) and exposes
 * identify / super-properties / track / reset. The SDK needs its official loader snippet (see
 * `loadSnippet`) — a bare `<script src>` throws "mixpanel object not initialized". Init targets the
 * EU project and masks Session Replay fully (a privacy rule). Pre-init calls are buffered and flushed.
 */
@Injectable({
  providedIn: 'root',
})
export class SgMixpanelService {
  private initialized = false;
  /** Actions requested before load()/init completes; flushed in order once the SDK is ready. */
  private readonly pending: Array<(mixpanel: MixpanelLib) => void> = [];

  /**
   * Init Mixpanel against the EU project and start masked Session Replay. Optionally identify the
   * user and register the super-properties BEFORE flushing buffered calls, so early buffered events
   * (e.g. `email_link_opened` queued by the email guard) already carry the identity. No-op if already
   * loaded or no token (an empty token is an expected, benign state in token-less environments).
   */
  load(token: string, userId?: string, properties?: MixpanelIdentityProperties): void {
    if (this.initialized || !token) {
      return;
    }

    this.loadSnippet();
    const mixpanel = window.mixpanel;
    if (!mixpanel) {
      console.error('[SgMixpanelService] Mixpanel snippet failed to initialize.');
      return;
    }

    mixpanel.init(token, {
      api_host: MIXPANEL_EU_API_HOST,
      // String mode so SPA route changes are captured, not just the first page load.
      track_pageview: 'url-with-path',
      persistence: 'localStorage',
      // --- Session Replay (full masking is a privacy rule) ---
      record_sessions_percent: 100,
      record_mask_all_text: true,
      record_mask_all_inputs: true,
      record_console: false,
      record_network: false,
      // Block media + canvas (charts render figures to canvas) on top of text/input masking.
      record_block_selector: 'img, video, canvas',
    });

    this.initialized = true;
    // Identify + register first, so buffered events flushed below already carry the super-properties.
    if (userId) {
      mixpanel.identify(userId);
      if (properties) {
        mixpanel.register({ ...properties });
      }
    }
    this.pending.forEach((action) => action(mixpanel));
    this.pending.length = 0;
  }

  /**
   * Update the identity super-properties (e.g. on legal-entity switch) without
   * re-identifying, so the same person carries the new entity going forward.
   * (The initial identify happens in `load()`.)
   */
  updateIdentityProperties(properties: Partial<MixpanelIdentityProperties>): void {
    this.run((mixpanel) => mixpanel.register({ ...properties }));
  }

  /** Track a business event under its fixed, agreed name. */
  track(eventName: string, properties: Record<string, unknown> = {}): void {
    this.run((mixpanel) => mixpanel.track(eventName, properties));
  }

  /**
   * End the Mixpanel identity and stop the recording (logout). Logging in as a
   * different user on the same browser is then recorded as a different person.
   */
  reset(): void {
    // Drop any un-flushed buffered actions so a later re-init in the same context can't replay the
    // previous user's events against the new identity.
    this.pending.length = 0;
    if (!this.initialized || !window.mixpanel) {
      return;
    }
    window.mixpanel.reset();
  }

  /**
   * Run an action now if the SDK is ready, otherwise buffer it until `load()` completes and flush
   * it then. Without this, events fired before init (e.g. the email-source guard on first
   * navigation, which can run before `load()`) would be silently dropped.
   */
  private run(action: (mixpanel: MixpanelLib) => void): void {
    if (this.initialized && window.mixpanel) {
      action(window.mixpanel);
      return;
    }
    if (this.pending.length < MAX_PENDING_ACTIONS) {
      this.pending.push(action);
    }
  }

  /**
   * Mixpanel's official browser loader snippet: creates the `window.mixpanel` stub and injects the
   * CDN library (`mixpanel-2-latest` — NOT version-pinned, so Session Replay masking option names
   * follow whatever the SDK's current build expects). Vendor code kept close to verbatim.
   */
  private loadSnippet(): void {
    if (window.mixpanel && window.mixpanel.__SV) {
      return;
    }
    /* eslint-disable */
    (function (f: any, b: any) {
      if (!b.__SV) {
        var e, g, i, h;
        window.mixpanel = b;
        b._i = [];
        b.init = function (e, f, c) {
          function g(a, d) {
            var b = d.split('.');
            2 == b.length && ((a = a[b[0]]), (d = b[1]));
            a[d] = function () {
              a.push([d].concat(Array.prototype.slice.call(arguments, 0)));
            };
          }
          var a = b;
          'undefined' !== typeof c ? (a = b[c] = []) : (c = 'mixpanel');
          a.people = a.people || [];
          a.toString = function (a) {
            var d = 'mixpanel';
            'mixpanel' !== c && (d += '.' + c);
            a || (d += ' (stub)');
            return d;
          };
          a.people.toString = function () {
            return a.toString(1) + '.people (stub)';
          };
          i =
            'disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove'.split(
              ' '
            );
          for (h = 0; h < i.length; h++) g(a, i[h]);
          var j = 'set set_once union unset remove delete'.split(' ');
          a.get_group = function () {
            function b(c) {
              d[c] = function () {
                var call2_args = arguments;
                var call2 = [c].concat(Array.prototype.slice.call(call2_args, 0));
                a.push([e, call2]);
              };
            }
            for (
              var d = {}, e = ['get_group'].concat(Array.prototype.slice.call(arguments, 0)), c = 0;
              c < j.length;
              c++
            )
              b(j[c]);
            return d;
          };
          b._i.push([e, f, c]);
        };
        b.__SV = 1.2;
        e = f.createElement('script');
        e.type = 'text/javascript';
        e.async = !0;
        e.src = 'https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js';
        g = f.getElementsByTagName('script')[0];
        g.parentNode.insertBefore(e, g);
      }
    })(document, window.mixpanel || []);
    /* eslint-enable */
  }
}
